/**
 * ImportProgress - Shows active import job progress
 *
 * Features:
 * - Collapsible progress bar with item count
 * - Expandable to show individual item status
 * - Status icons for each import item
 * - Error category labels for failed items
 */

import { ChevronDown, ChevronUp, Check, X, Loader2, Download } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/utils';
import type { ImportJob, ImportJobItem, ImportErrorCategory, ImportJobItemStatus } from '@/types/meta';

interface ImportProgressProps {
  job: ImportJob;
  className?: string;
}

/**
 * Get icon for import item status
 */
function getItemIcon(status: ImportJobItemStatus) {
  switch (status) {
    case 'completed':
      return <Check className="h-4 w-4 text-success" />;
    case 'failed':
      return <X className="h-4 w-4 text-danger" />;
    case 'pending':
      return <div className="h-4 w-4 rounded-full border-2 border-muted" />;
    default:
      // downloading, uploading, creating
      return <Loader2 className="h-4 w-4 animate-spin text-brand" />;
  }
}

/**
 * Get human-readable label for error category
 */
function getErrorLabel(category?: ImportErrorCategory): string {
  switch (category) {
    case 'too_large':
      return 'Video too large';
    case 'download_failed':
      return 'Download failed';
    case 'format_unsupported':
      return 'Format not supported';
    case 'duplicate':
      return 'Already imported';
    case 'page_admin_required':
      return 'Page admin access required';
    case 'meta_error':
      return 'Meta API error';
    case 'unexpected_error':
      return 'Unexpected error';
    default:
      return 'Import failed';
  }
}

/**
 * Get detailed error message for tooltip
 */
function getErrorTooltip(category?: ImportErrorCategory): string | undefined {
  switch (category) {
    case 'page_admin_required':
      return 'This video is from a Facebook Page (like a Reel). You need admin or editor access on that Page to import it. Try uploading the video directly instead.';
    case 'too_large':
      return 'Video exceeds the maximum file size limit (200MB).';
    case 'unexpected_error':
      return 'An unexpected import error occurred. Try again, or contact support if it repeats.';
    default:
      return undefined;
  }
}

export function ImportProgress({ job, className }: ImportProgressProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const processedCount = job.completedItems + job.failedItems;
  const progress = job.totalItems > 0
    ? (processedCount / job.totalItems) * 100
    : 0;

  const isActive = job.status === 'queued' || job.status === 'processing';

  return (
    <div
      className={cn(
        'rounded-lg border border-border-subtle bg-surface p-4 shadow-sm',
        className
      )}
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between"
      >
        <div className="flex items-center gap-3">
          {isActive ? (
            <Loader2 className="h-5 w-5 animate-spin text-brand" />
          ) : (
            <Download className="h-5 w-5 text-muted" />
          )}
          <div className="text-left">
            <div className="font-medium text-primary">
              {isActive
                ? `Importing ${processedCount}/${job.totalItems} videos`
                : `Import ${job.status}`}
            </div>
            <div className="text-sm text-secondary">
              {job.completedItems} completed, {job.failedItems} failed
            </div>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-muted" />
        ) : (
          <ChevronDown className="h-5 w-5 text-muted" />
        )}
      </button>

      {/* Progress bar */}
      <div className="mt-3 h-2 w-full rounded-full bg-surface-subtle">
        <div
          className={cn(
            'h-2 rounded-full transition-all duration-300',
            job.status === 'completed'
              ? 'bg-success'
              : job.status === 'failed'
              ? 'bg-danger'
              : job.status === 'partial'
              ? 'bg-warning'
              : 'bg-brand'
          )}
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Expanded item list */}
      {isExpanded && (
        <div className="mt-4 max-h-48 space-y-2 overflow-y-auto">
          {job.items.map((item: ImportJobItem) => (
            <div
              key={item.adId}
              className="flex items-center justify-between rounded-md bg-surface-subtle px-3 py-2"
            >
              <div className="flex items-center gap-2">
                {getItemIcon(item.status)}
                <span className="text-sm text-primary">
                  {item.adName || item.adId}
                </span>
              </div>
              {item.status === 'failed' && (
                <span
                  className="cursor-help text-xs text-danger"
                  title={getErrorTooltip(item.errorCategory)}
                >
                  {getErrorLabel(item.errorCategory)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
